import { useQuery } from '@tanstack/react-query';
import config from '../config';
import { useAlgo } from '../hooks/useAlgo';
import { useKittiesStore } from '../hooks/useKittiesStore';
import useTraitSwapStore from '../hooks/useTraitSwapStore';
import { useUserAssetsStore } from '../hooks/useUserAssetsStore';
import Alert from './Alert';
import Spinner from './Spinner';
import Steps from './Steps';
import TraitSwapFloatingActions from './TraitSwapFloatingActions';
import TraitSwapPreview from './TraitSwapPreview';
import TraitSwapSelect from './TraitSwapSelect';
import { StatCard } from './StatCard';
import formatNumber from '../utils/formatNumber';
import ConnectWalletMessage from './ConnectWalletMessage';

const TraitSwap = () => {
  const { address } = useAlgo();
  const { activeTab, setActiveTab, selectedNfts } = useTraitSwapStore();
  const { myKitties, myKittiesLoading } = useKittiesStore();
  const { isLoading } = useUserAssetsStore();

  const myKittiesForSwap = myKitties
    ?.filter((kitty) => kitty.gen === 4)
    .filter((kitty) => kitty.properties.Special === 'None')
    .filter((kitty) => {
      const bannedTraits = config.bannedTraits;
      const traitValues = Object.values(kitty.properties);
      return !bannedTraits.some((trait) => traitValues.includes(trait));
    });

  const { data: traitSwapStats } = useQuery({
    queryKey: ['traitSwapStats'],
    queryFn: () => fetch('/api/trait-swap/stats').then((res) => res.json()),
  });
  const totalTraitSwaps = traitSwapStats?.total || 0;

  const { data: myTraitSwapStats } = useQuery({
    queryKey: ['myTraitSwapStats', address],
    queryFn: () => fetch(`/api/trait-swap/stats?address=${address}`).then((res) => res.json()),
  });
  const myTotalTraitSwaps = myTraitSwapStats?.total || 0;

  const steps = [
    { id: 0, name: 'Select 2 Shitty Kitties', onClick: () => setActiveTab(0) },
    {
      id: 1,
      name: 'Select traits to swap',
      onClick: () => {
        if (selectedNfts.length !== 2 && activeTab === 0) {
          return;
        }
        setActiveTab(1);
      },
    },
  ];

  if (process.env.NEXT_PUBLIC_TRAIT_SWAP_IS_ENABLED !== 'true') {
    return (
      <div className='max-w-lg mx-auto mt-10 text-center'>
        <Alert type='info' className={'flex text-center justify-center'}>
          Coming soon...
        </Alert>
      </div>
    );
  }

  if (myKittiesLoading || isLoading) {
    return (
      <div className='flex justify-center mt-20'>
        <Spinner />
      </div>
    );
  }

  if (myKittiesForSwap.length === 0) {
    return (
      <div className='max-w-lg mx-auto mt-10 text-center'>
        <Alert type='danger' className={'flex text-center justify-center'}>
          You must own a Shitty Kitty to use the Trait Swap.<br></br> Check out the{' '}
          <a href={`/marketplace?collection=Fourth Litter`} className='font-bold'>
            Marketplace
          </a>{' '}
          to purchase a Shitty Kitty.
        </Alert>
      </div>
    );
  }

  return (
    <div className='relative pb-36 mt-8'>
      <div className='flex mb-6 gap-4 justify-end'>
        <div className='min-w-[150px]'>
          <StatCard title='My Swaps' value={formatNumber(myTotalTraitSwaps)} />
        </div>
        <div className='min-w-[150px]'>
          <StatCard title='Total Swaps' value={formatNumber(totalTraitSwaps)} />
        </div>
      </div>
      <div className='mb-6'>
        <Steps steps={steps} activeStep={activeTab} />
      </div>
      {activeTab === 0 && <TraitSwapSelect kitties={myKittiesForSwap} />}
      {activeTab === 1 && <TraitSwapPreview />}

      <TraitSwapFloatingActions />
    </div>
  );
};

export default TraitSwap;
